// AudioSpectrogram.js - Fixed download functions
import React, { useState, useEffect } from 'react';
import { Download, FilePlus, ArrowLeft, X , ChevronRight, ChevronLeft, LoaderCircle} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAudioBuzzRecordById,downloadRecordingInterfaceZip } from '../../services/apiService';
import Swal from 'sweetalert2';

const Loader = () => (
  <div className="flex items-center justify-center min-h-screen bg-black">
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const AudioSpectrogram = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { audioIds = [], modelName = 'Unknown Model', recordId } = location.state || {};

  const [currentIndex, setCurrentIndex] = useState(0);
  const [audioData, setAudioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);


   useEffect(() => {
  setIsImageLoading(true);
}, [currentIndex]);


  // Helper function to extract filename from S3 URL
const getFilenameFromUrl = (url) => {
  if (!url) return 'download';
  try {
    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1];
    // Decode URL encoding (e.g., %20 to space)
    return decodeURIComponent(filename);
  } catch (error) {
    console.error('Error extracting filename:', error);
    return 'download';
  }
};
  useEffect(() => {
    if (!audioIds || audioIds.length === 0) {
      setError('No audio IDs provided');
      setLoading(false);
      return;
    }

    const fetchAudioData = async () => {
      try {
        setLoading(true);
        setError(null);
        const currentId = audioIds[currentIndex];
        const result = await getAudioBuzzRecordById(currentId);
        if (result.success && result.data) {
          setAudioData(result.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching audio data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAudioData();
  }, [currentIndex, audioIds]);

  const handleBackClick = () => {
    navigate('/audioReviewRecord');
  };

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < audioIds.length - 1) setCurrentIndex(currentIndex + 1);
  };

// Helper function to download file from URL
const downloadFileFromUrl = async (url, filename) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    }, 100);
  } catch (error) {
    console.error('Download error:', error);
    
    // Fallback: Open in new tab if CORS still fails
    try {
      window.open(url, '_blank');
    } catch (fallbackError) {
    alert(`Failed to download ${filename}: ${error.message}`);
    }
    }
  };
  const handleDownloadAudio = async () => {
    if (!audioData?.audio_s3_url) {
      alert('Audio file URL not available');
      return;
    }

    setIsDownloading(true);
    try {
      const filename = getFilenameFromUrl(audioData.audio_s3_url);
      await downloadFileFromUrl(audioData.audio_s3_url, filename);
    } catch (error) {
      console.error('Error downloading audio:', error);
    } finally {
      setIsDownloading(false);
  }
};

const handleDownloadTXT = async () => {
  if (!audioData?.audio_txt_s3_url) {
    alert('TXT file URL not available');
    return;
  }

  setIsDownloading(true);
  try {
    const originalFilename = getFilenameFromUrl(audioData.audio_txt_s3_url);
    
    // Determine the new filename based on conditions
    let newFilename = originalFilename;
    
    const predictionBinary = audioData.prediction_binary;
    const hJudgment = audioData.h_judgment;
    
    // Convert h_judgment to boolean for comparison
    const isHJudgmentTrue = hJudgment === true || hJudgment === 'true';
    const isHJudgmentFalse = hJudgment === false || hJudgment === 'false';
    
    // Extract base parts from original filename
    // Example: est_042_255HZ_no_audio_buzz_1770282991631.txt
    const match = originalFilename.match(/^(est_)?(.+?)_(no_audio_buzz|audio_buzz)_(\d+)(\.txt)$/i);
    
    if (match) {
      const prefix = match[1] || ''; // 'est_' or empty
      const baseName = match[2]; // '042_255HZ'
      const timestamp = match[4]; // '1770282991631'
      const extension = match[5]; // '.txt'
      
      let newStatus = '';
      
      // Apply your conditions
      if (predictionBinary === "No Audio Buzz" && isHJudgmentTrue) {
        newStatus = 'audio_buzz_corrected';
      } else if (predictionBinary === "No Audio Buzz" && isHJudgmentFalse) {
        newStatus = 'no_audio_buzz';
      } else if (predictionBinary === "Audio Buzz" && isHJudgmentTrue) {
        newStatus = 'audio_buzz';
      } else if (predictionBinary === "Audio Buzz" && isHJudgmentFalse) {
        newStatus = 'no_audio_buzz';
      }
      
      // Construct new filename
      if (newStatus) {
        newFilename = `${prefix}${baseName}_${newStatus}_${timestamp}${extension}`;
      }
    }
    
    await downloadFileFromUrl(audioData.audio_txt_s3_url, newFilename);
  } catch (error) {
    console.error('Error downloading TXT:', error);
  } finally {
    setIsDownloading(false);
  }
};

const handleDownloadSpectrum = async () => {
  if (!audioData?.spectrum_graph_s3_url) {
    alert('Spectrum graph URL not available');
    return;
  }

  setIsDownloading(true);
  try {
    const filename = getFilenameFromUrl(audioData.spectrum_graph_s3_url);
    await downloadFileFromUrl(audioData.spectrum_graph_s3_url, filename);
  } catch (error) {
    console.error('Error downloading spectrum:', error);
  } finally {
    setIsDownloading(false);
  }
};

const handleDownloadAll = async () => {
    if (!recordId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Record ID not available',
        background: '#1f2937',
        color: '#fff',
        confirmButtonColor: '#3b82f6',
      });
    return;
  }

  setIsDownloading(true);
  try {
      console.log('Downloading zip for main record:', recordId);
      const result = await downloadRecordingInterfaceZip(recordId);
      
      if (result.success && result.data) {
        const blob = new Blob([result.data], { type: 'application/zip' });
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `recording_${recordId}_${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(link);
        }, 100);
        
        Swal.fire({
          icon: 'success',
          title: 'Download Complete!',
          text: 'Zip file downloaded successfully',
          background: '#f9f9faff',
          color: '#0e0808ff',
          confirmButtonColor: '#10b981',
          timer: 3000,
          timerProgressBar: true,
        });
      }
  } catch (error) {
    console.error('Error downloading zip:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: `Failed to download zip file: ${error.message}`,
        background: '#1f2937',
        color: '#fff',
        confirmButtonColor: '#ef4444',
      });
  } finally {
    setIsDownloading(false);
  }
};

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-black text-white flex items-center justify-center">
        <div className="text-xl text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!audioData) {
    return (
      <div className="min-h-screen w-full bg-black text-white flex items-center justify-center">
        <div className="text-xl">No data available</div>
      </div>
    );
  }

  const baseFrequency = audioData.base_frequency || 95;
  const harmonics = audioData.harmonics || [];
  const firstFiveHarmonics = harmonics.slice(0, 5);
  const aiPrediction = audioData.prediction_binary || 'N/A';

// Convert boolean h_judgment to display text
    let humanJudgment = 'N/A';
      if (audioData.h_judgment === true || audioData.h_judgment === 'true') {
        humanJudgment = 'Audio Buzz';
      } else if (audioData.h_judgment === false || audioData.h_judgment === 'false') {
        humanJudgment = 'No Buzz';
      } else if (audioData.h_judgment === null) {
        humanJudgment = 'Not Verified';
      }

  const getHarmonicLabel = (harmonicNumber) => {
    if (harmonicNumber === 1) return 'Basic Frequency';
    const labels = ['Second', 'Third', 'Fourth', 'Fifth'];
    return `${labels[harmonicNumber - 2]} Harmonic`;
  };

  return (
    <div className="min-h-screen w-full bg-black text-white md:p-4 lg:p-0 overflow-auto flex flex-col">
      <div className="flex flex-col justify-between items-left lg:mb-4 px-2 md:px-4 lg:px-0 gap-3 lg:gap-4 mt-3 flex-shrink-0">
        <div className="flex items-center sm:gap-20 gap-5 p-2 ml-3 -mb-5">
          <ArrowLeft
            className="w-5 h-5 md:w-9 md:h-9 text-white -ml-4 cursor-pointer"
            strokeWidth={2}
            onClick={handleBackClick}
          />
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
            {modelName}
          </h1>
        </div>

        <div className="flex flex-wrap justify-left sm:gap-10 gap-2 w-full sm:w-auto p-2">
          <button
            onClick={handleDownloadTXT}
            disabled={isDownloading || !audioData?.audio_txt_s3_url}
            className="w-full md:w-auto px-4 py-2 md:px-6 md:py-3 bg-gray-800 text-white text-xs md:text-lg font-medium rounded-md hover:bg-gray-700 transition-colors border border-gray-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FilePlus className="w-6 h-6" />
            {isDownloading ? 'Downloading...' : 'Download TXT'}
          </button>
          <button
            onClick={handleDownloadAudio}
            disabled={isDownloading || !audioData?.audio_s3_url}
            className="w-full md:w-auto px-4 py-2 md:px-6 md:py-3 bg-gray-800 text-white text-xs md:text-lg font-medium rounded-md hover:bg-gray-700 transition-colors border border-gray-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FilePlus className="w-6 h-6" />
            {isDownloading ? 'Downloading...' : 'Download Recorded Audio'}
          </button>
          <button
            onClick={handleDownloadSpectrum}
            disabled={isDownloading || !audioData?.spectrum_graph_s3_url}
            className="w-full md:w-auto px-4 py-2 md:px-6 md:py-3 bg-gray-800 text-white text-xs md:text-lg font-medium rounded-md hover:bg-gray-700 transition-colors border border-gray-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FilePlus className="w-6 h-6" />
            {isDownloading ? 'Downloading...' : 'Download Equal-Space Diagrams'}
          </button>
          <button
            onClick={handleDownloadAll}
            disabled={isDownloading || !recordId}
            className="w-full md:w-auto px-4 py-2 md:px-6 md:py-3 bg-blue-500 text-white text-xs md:text-lg font-medium rounded-md hover:bg-blue-600 transition-colors border border-blue-400 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-6 h-6" />
            {isDownloading ? 'Downloading...' : 'Download All'}
          </button>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg shadow-lg p-4 md:p-6 lg:p-5 mx-2 md:mx-4 lg:mx-0 flex-1 mb-5 ">
        {/* AI Detection Result Section */}
        <div className="mb-6 pb-4 border-b border-gray-700">
        <h2 className="text-lg md:text-xl font-bold text-white mb-3">
            AI Detection Result: <span className="text-blue-400">{aiPrediction}</span>
        </h2>
        </div>

        <p className="text-gray-300 mb-1 md:mb-1 text-sm md:text-lg leading-relaxed">
          1. The possible audio buzz may occur at the fundamental frequency of{' '}
          {baseFrequency} Hz, as indicated by the spectrogram and harmonic
          record shown below.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-3 md:mb-4">
          {/* <div
            className="bg-gray-800 rounded-lg flex justify-center items-center min-h-[200px] md:min-h-[280px] lg:min-h-[400px] w-full cursor-pointer"
            onClick={() => setIsPreviewOpen(true)}
          >
            {audioData.spectrum_graph_s3_url ? (
              <img
                src={audioData.spectrum_graph_s3_url}
                alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML =
                    '<div class="text-gray-500">Image not available</div>';
                }}
              />
            ) : (
              <div className="text-gray-500">No spectrum image available</div>
            )}
          </div> */}

            {/* <div
              className="
                bg-gray-800 rounded-lg flex justify-center items-center
                min-h-[160px] md:min-h-[220px] lg:min-h-[300px]
                w-full cursor-pointer relative
              "
              onClick={() => setIsPreviewOpen(true)}
            >
              
              {isImageLoading && audioData.spectrum_graph_s3_url && (
                <LoaderCircle className="w-10 h-10 text-white animate-spin absolute" />
              )}

              {audioData.spectrum_graph_s3_url ? (
                <img
                  src={audioData.spectrum_graph_s3_url}
                  alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
                  className={`
                max-w-full max-h-full
                object-contain
                scale-100 md:scale-100   
                transition-transform duration-300
                ${isImageLoading ? "opacity-0" : "opacity-100"}
              `}
                  onLoad={() => setIsImageLoading(false)}
                  onError={(e) => {
                    setIsImageLoading(false);
                    e.target.onerror = null;
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML =
                      '<div class="text-gray-500">Image not available</div>';
                  }}
                />
              ) : (
                <div className="text-gray-500">No spectrum image available</div>
              )}
            </div> */}
            <div className="bg-gray-800 rounded-lg flex justify-center items-center 
                min-h-[200px] md:min-h-[280px] lg:min-h-[400px] 
                w-full cursor-pointer relative overflow-hidden
              "
              onClick={() => setIsPreviewOpen(true)}
            >
              {audioData?.spectrum_graph_s3_url ? (
                <>
                  {/* Loader overlay – always shown when image is loading */}
                  {isImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm z-20">
                      <LoaderCircle className="w-12 h-12 text-blue-400 animate-spin" />
                    </div>
                  )}
                  <img
                    key={currentIndex} 
                    src={audioData.spectrum_graph_s3_url}
                    alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
                    className={`
                      max-w-full max-h-full object-contain
                      transition-opacity duration-500 ease-in-out
                      ${isImageLoading ? 'opacity-0' : 'opacity-100'}
                    `}
                    // Reset loading state when index changes → show loader again
                    onLoad={() => {
                      setIsImageLoading(false);
                      console.log('Image loaded successfully');
                    }}
                    onError={(e) => {
                      console.error('Image load error:', e);
                      setIsImageLoading(false);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </>
              ) : (
                <div className="text-gray-500 text-center p-4">
                  No spectrum image available
                </div>
              )}
            </div>

          <div className="overflow-x-auto w-full mb-5">
            <table className="min-w-full bg-black-800 border border-gray-300 rounded-lg">
              <thead className="bg-black-700">
                <tr>
                  <th className="px-2 md:px-4 sm:py-6 text-left text-md font-medium text-white uppercase tracking-wider border-b border-r border-gray-300" />
                  <th className="px-2 md:px-4 sm:py-6 text-left text-xs sm:text-sm md:text-base font-medium text-white uppercase tracking-wider border-b border-r border-gray-300">
                    Frequency (Hz)
                  </th>
                  <th className="px-2 md:px-4 sm:py-6 text-left text-xs sm:text-sm md:text-base font-medium text-white uppercase tracking-wider border-b border-r border-gray-300">
                    Energy (dB)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-x divide-gray-300">
                {firstFiveHarmonics.length > 0 ? (
                  firstFiveHarmonics.map((harmonic, idx) => (
                    <tr key={harmonic._id || idx}>
                      <td className="px-2 md:px-4 sm:py-4 text-md text-white border-r border-gray-300">
                        {getHarmonicLabel(harmonic.harmonic_number)}
                      </td>
                      <td className="px-2 md:px-4 sm:py-4 text-md text-white border-r border-gray-300">
                        {Math.round(harmonic.frequency)}
                      </td>
                      <td className="px-2 md:px-4 sm:py-4 text-md text-white border-r border-gray-300">
                        {harmonic.db_level.toFixed(0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-2 md:px-4 sm:py-4 text-md text-gray-400 text-center border-r border-gray-300"
                    >
                      No harmonic data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <table className="min-w-full bg-black-800 divide-x divide-y divide-gray-300 rounded-lg mt-6 border border-gray-300">
              <thead className="bg-black-700">
                <tr>
                  <th className="px-2 md:px-4 py-3 text-left text-md font-medium text-white uppercase tracking-wider border-r border-gray-300" />
                  <th className="px-2 md:px-4 py-3 text-left text-xs sm:text-sm md:text-base font-medium text-white uppercase tracking-wider border-r border-gray-300">
                    Energy (dB)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-x divide-gray-300">
                <tr className="bg-black-700">
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    AVG (excluding basic frequency)
                  </td>
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    {audioData.mean !== null && audioData.mean !== undefined
                      ? audioData.mean.toFixed(0)
                      : 'N/A'}
                  </td>
                </tr>
                <tr className="bg-black-700">
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    Standard Deviation
                  </td>
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    {audioData.std !== null && audioData.std !== undefined
                      ? audioData.std.toFixed(0)
                      : 'N/A'}
                  </td>
                </tr>
                <tr className="bg-black-700">
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    AVG Standard Deviation
                  </td>
                  <td className="px-2 md:px-4 py-3 text-md font-medium text-white border-r border-gray-300">
                    {audioData.mean !== null && audioData.std !== null
                      ? (audioData.mean + audioData.std).toFixed(0)
                      : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="sm:text-xl text-white mt-1 md:mt-2 text-sm">
              Remark: Calculation of both the average and standard deviation
              excludes the fundamental frequency.
            </p>
          </div>
        </div>
        {/* Human Judgment Section */}
        <div className="mt-6 pt-4 border-t border-gray-700 mb-4 md:mb-12">
          <h2 className="text-lg md:text-xl font-bold text-white mb-10">
            Human Judgment: <span className="text-blue-400">{humanJudgment}</span>
          </h2>
        </div>
      </div>

      {/* <div
        className="fixed bottom-0 left-0 right-0 w-full py-4 md:py-4 lg:py-6 bg-black z-50 px-4 md:px-6 lg:px-8 flex justify-center items-center gap-2 text-xs md:text-sm text-gray-400 border-t border-gray-500 pointer-events-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <button
          className="px-3 py-2 rounded hover:bg-gray-700 transition-colors border border-gray-500 mb-2 -mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentIndex === 0}
          onClick={handlePrevious}
          aria-label="Previous"
        >
          &lt;
        </button>

        <span className="min-w-[55px] text-center mb-2 -mt-3">
          {currentIndex + 1} / {audioIds.length}
        </span>

        <button
          className="px-3 py-2 rounded hover:bg-gray-700 transition-colors border border-gray-500 mb-2 -mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentIndex === audioIds.length - 1}
          onClick={handleNext}
          aria-label="Next"
        >
          &gt;
        </button>
      </div> */}

{/* pagination */}

      <div
  className="fixed bottom-0 left-0 right-0 w-full py-3 bg-black z-50 px-3
             flex justify-center items-center gap-2
             text-xs md:text-sm text-gray-400
             border-t border-gray-500"
  style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
>
  {/* Prev */}
  <button
    onClick={handlePrevious}
    disabled={currentIndex === 0}
    className={`sm:p-2 p-1 rounded-full mb-5 ${
      currentIndex === 0
        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
        : 'bg-teal-600 hover:bg-teal-700 text-white'
    }`}
    aria-label="Previous"
  >
    <ChevronLeft size={18} />
  </button>

  {/* Pages */}
  <div className="flex gap-1 max-w-[75vw] overflow-x-auto scrollbar-hide ">
{(() => {
  const totalPages = audioIds.length;
  const curr = currentIndex + 1;
  const pages = [];

  // Always show first page
  pages.push(1);

  let start = curr - 1;
  let end = curr + 1;

  // ✅ Near start → show 2,3,4
  if (curr <= 3) {
    start = 2;
    end = Math.min(4, totalPages - 1);
  }

  // Near end → show last-3, last-2, last-1
  if (curr >= totalPages - 2) {
    start = Math.max(totalPages - 3, 2);
    end = totalPages - 1;
  }

  // Left dots (gap exists)
  if (start > 2) {
    pages.push("...");
  }

  // Middle pages
  for (let i = start; i <= end; i++) {
    if (i > 1 && i < totalPages) {
      pages.push(i);
    }
  }

  // Right dots (gap exists)
  if (end < totalPages - 1) {
    pages.push("...");
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages.map((page, index) =>
    page === "..." ? (
      <span
        key={`ellipsis-${index}`}
        className="flex items-center justify-center sm:w-8 w-6 sm:h-8 h-6 text-gray-300 flex-shrink-0"
      >
        ...
      </span>
    ) : (
      <button
        key={page}
        onClick={() => setCurrentIndex(page - 1)}
        className={`sm:w-8 w-6 sm:h-8 h-6 rounded-full flex-shrink-0 mb-5 ${
          curr === page
            ? "bg-teal-600 text-white"
            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
        }`}
      >
        {page}
      </button>
    )
  );
})()}


  </div>

  {/* Next */}
  <button
    onClick={handleNext}
    disabled={currentIndex === audioIds.length - 1}
    className={`sm:p-2 p-1 rounded-full mb-5 ${
      currentIndex === audioIds.length - 1
        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
        : 'bg-teal-600 hover:bg-teal-700 text-white'
    }`}
    aria-label="Next"
  >
    <ChevronRight size={18} />
  </button>
</div>








      {isPreviewOpen && audioData.spectrum_graph_s3_url && (
  <div
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
    onClick={(e) => {
      if (e.target === e.currentTarget) setIsPreviewOpen(false);
    }}
  >
    <div className="relative">

      {/* Close button – SAME styles */}
      <button
        onClick={() => setIsPreviewOpen(false)}
        className="
          absolute -top-15 -right-3
          sm:-top-14 sm:-right-5
          bg-gray-800 hover:bg-gray-700
          text-white rounded-full
          w-9 h-9 sm:w-10 sm:h-10
          flex items-center justify-center
          text-2xl font-bold z-20
        "
      >
        ×
      </button>

      {/* Preview container – SAME styles */}
      <div className="bg-gray-900 max-w-6xl max-h-[98vh] overflow-auto relative flex items-center justify-center">

        {/* Loader – SAME styles */}
        {isImageLoading && (
          <LoaderCircle className="w-10 h-10 text-white animate-spin absolute" />
        )}

        {/* Preview image – SAME styles */}
        <img
          src={audioData.spectrum_graph_s3_url}
          alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
          onLoad={() => setIsImageLoading(false)}
          onError={() => setIsImageLoading(false)}
          className={`
            w-full max-w-full
            h-auto
            max-h-[95vh]
            sm:max-h-[90vh]
            lg:max-h-[98vh]
            object-contain
            ${isImageLoading ? "opacity-0" : "opacity-100"}
            transition-opacity duration-300
          `}
        />
      </div>
    </div>
  </div>
)}
      {/* Download Loader Overlay */}
      {isDownloading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-4">
            <LoaderCircle className="w-12 h-12 text-blue-500 animate-spin" />
            <p className="text-white text-lg font-semibold">Downloading...</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default AudioSpectrogram;
